import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Pencil, Trash, ChevronDown, ChevronRight, Plus, HelpCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StreamList } from "@/components/nurse/streams/StreamList";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const classFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  schoolId: z.string().min(1, "School is required"),
  levelId: z.string().min(1, "Level is required"),
  nextClassId: z.string().optional(),
  sequenceOrder: z.number().min(1).max(100),
  leadsToGraduation: z.boolean().default(false),
});

export function ClassStreamManager() {
  const [selectedSchool, setSelectedSchool] = useState<string>("all");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<any | null>(null);
  const [expandedLevels, setExpandedLevels] = useState<string[]>([]);
  const [expandedClasses, setExpandedClasses] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof classFormSchema>>({
    resolver: zodResolver(classFormSchema),
    defaultValues: {
      name: "",
      schoolId: "",
      levelId: "",
      nextClassId: "none",
      sequenceOrder: 1,
      leadsToGraduation: false,
    },
  });

  const { data: schools } = useQuery({
    queryKey: ['schools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const { data: levels } = useQuery({
    queryKey: ['levels', selectedSchool],
    queryFn: async () => {
      let query = supabase
        .from('levels')
        .select('*')
        .order('sequence_order');
      
      if (selectedSchool !== 'all') {
        query = query.eq('school_id', selectedSchool);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const { data: classes, isLoading } = useQuery({
    queryKey: ['school_classes', selectedSchool],
    queryFn: async () => {
      let query = supabase
        .from('school_classes')
        .select(`
          *,
          schools(name),
          levels(name),
          next_class:next_class_id(name)
        `)
        .order('sequence_order');
      
      if (selectedSchool !== 'all') {
        query = query.eq('school_id', selectedSchool);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const createClass = useMutation({
    mutationFn: async (values: z.infer<typeof classFormSchema>) => {
      // Get all classes for this level ordered by sequence order
      const { data: existingClasses, error: fetchError } = await supabase
        .from('school_classes')
        .select('sequence_order')
        .eq('level_id', values.levelId)
        .order('sequence_order', { ascending: false });

      if (fetchError) throw fetchError;

      // Find the next available sequence order
      let nextSequenceOrder = 1;
      if (existingClasses && existingClasses.length > 0) {
        const usedOrders = new Set(existingClasses.map(c => c.sequence_order));
        while (usedOrders.has(nextSequenceOrder)) {
          nextSequenceOrder++;
        }
      }

      const { error } = await supabase
        .from('school_classes')
        .insert({
          name: values.name,
          school_id: values.schoolId,
          level_id: values.levelId,
          next_class_id: values.nextClassId === "none" ? null : values.nextClassId,
          sequence_order: nextSequenceOrder,
          leads_to_graduation: values.leadsToGraduation,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school_classes'] });
      form.reset();
      setIsDialogOpen(false);
      toast({
        title: "Success",
        description: "Class created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create class",
        variant: "destructive",
      });
    },
  });

  const updateClass = useMutation({
    mutationFn: async (values: z.infer<typeof classFormSchema>) => {
      // If level_id changed or sequence_order changed, we need to check for conflicts
      if (selectedClass.level_id !== values.levelId || 
          selectedClass.sequence_order !== values.sequenceOrder) {
        
        // Get all classes for the target level
        const { data: existingClasses, error: fetchError } = await supabase
          .from('school_classes')
          .select('sequence_order')
          .eq('level_id', values.levelId)
          .neq('id', selectedClass.id) // Exclude current class
          .order('sequence_order', { ascending: false });

        if (fetchError) throw fetchError;

        // Check if the requested sequence order is already taken
        if (existingClasses?.some(c => c.sequence_order === values.sequenceOrder)) {
          throw new Error(`Sequence order ${values.sequenceOrder} is already taken in this level`);
        }
      }

      const { error } = await supabase
        .from('school_classes')
        .update({
          name: values.name,
          school_id: values.schoolId,
          level_id: values.levelId,
          next_class_id: values.nextClassId === "none" ? null : values.nextClassId,
          sequence_order: values.sequenceOrder,
          leads_to_graduation: values.leadsToGraduation,
        })
        .eq('id', selectedClass.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school_classes'] });
      form.reset();
      setIsDialogOpen(false);
      setSelectedClass(null);
      toast({
        title: "Success",
        description: "Class updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update class",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (classId: string) => {
      setIsDeleting(true);
      try {
        const { error } = await supabase
          .from('school_classes')
          .delete()
          .eq('id', classId);
        
        if (error) throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school_classes'] });
      toast({
        title: "Success",
        description: "Class deleted successfully",
      });
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete class",
        variant: "destructive",
      });
      setDeleteConfirm(null);
    },
  });

  const handleEdit = (classItem: any) => {
    form.reset({
      name: classItem.name,
      schoolId: classItem.school_id,
      levelId: classItem.level_id,
      nextClassId: classItem.next_class_id || "none",
      sequenceOrder: classItem.sequence_order,
      leadsToGraduation: classItem.leads_to_graduation || false,
    });
    setSelectedClass(classItem);
    setIsDialogOpen(true);
  };

  const onSubmit = (values: z.infer<typeof classFormSchema>) => {
    if (selectedClass) {
      updateClass.mutate(values);
    } else {
      createClass.mutate(values);
    }
  };

  const toggleLevel = (levelId: string) => {
    setExpandedLevels(prev => 
      prev.includes(levelId) 
        ? prev.filter(id => id !== levelId)
        : [...prev, levelId]
    );
  };

  const toggleClass = (classId: string) => {
    setExpandedClasses(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  // Group classes by level
  const classesGroupedByLevel = classes?.reduce((acc: any, classItem: any) => {
    const levelId = classItem.level_id;
    if (!acc[levelId]) {
      acc[levelId] = [];
    }
    acc[levelId].push(classItem);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Select value={selectedSchool} onValueChange={setSelectedSchool}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select School" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Schools</SelectItem>
            {schools?.map((school) => (
              <SelectItem key={school.id} value={school.id}>
                {school.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Class
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedClass ? "Edit Class" : "Add New Class"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="schoolId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>School</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a school" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {schools?.map((school) => (
                            <SelectItem key={school.id} value={school.id}>
                              {school.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="levelId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Level</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {levels?.map((level) => (
                            <SelectItem key={level.id} value={level.id}>
                              {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="leadsToGraduation"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (checked) {
                              form.setValue("nextClassId", "none");
                            }
                          }}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Graduating Class
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sequenceOrder"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center space-x-2">
                        <FormLabel>Sequence Order</FormLabel>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-4 w-4">
                                <HelpCircle className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent 
                              className="max-w-[300px] bg-white border border-gray-200 shadow-lg p-2 text-foreground"
                            >
                              <p>Sequence Order determines the order of classes within the same level 
                                 (e.g., Form 1A comes before Form 1B). This is used for display purposes 
                                 and organizing classes within each level.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field} 
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="nextClassId"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center space-x-2">
                        <FormLabel>Next Class</FormLabel>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-4 w-4">
                                <HelpCircle className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent 
                              className="max-w-[300px] bg-white border border-gray-200 shadow-lg p-2 text-foreground"
                            >
                              <p>Next Class defines where students will be promoted to when moving between levels 
                                 (e.g., from Form 1 to Form 2). This is specifically used for the promotion process 
                                 between different levels.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || "none"}
                        disabled={form.watch("leadsToGraduation")}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select next class" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {classes?.map((classItem) => (
                            <SelectItem key={classItem.id} value={classItem.id}>
                              {classItem.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button 
                    type="submit" 
                    disabled={createClass.isPending || updateClass.isPending}
                  >
                    {(createClass.isPending || updateClass.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {selectedClass ? "Update Class" : "Create Class"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {levels?.map((level) => (
            <Collapsible 
              key={level.id} 
              open={expandedLevels.includes(level.id)}
              onOpenChange={(isOpen) => {
                if (isOpen) {
                  setExpandedLevels(prev => [...prev, level.id]);
                } else {
                  setExpandedLevels(prev => prev.filter(id => id !== level.id));
                }
              }}
            >
              <Card>
                <CardHeader className="cursor-pointer">
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <CardTitle className="flex items-center">
                      {expandedLevels.includes(level.id) ? (
                        <ChevronDown className="h-5 w-5 mr-2" />
                      ) : (
                        <ChevronRight className="h-5 w-5 mr-2" />
                      )}
                      {level.name}
                    </CardTitle>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent>
                    <div className="space-y-4">
                      {classesGroupedByLevel?.[level.id]?.map((classItem: any) => (
                        <Card key={classItem.id} className="border border-gray-200">
                          <CardHeader className="cursor-pointer py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <span className="font-medium">{classItem.name}</span>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(classItem)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteConfirm(classItem.id)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="text-sm text-gray-500 mb-4">
                              <p>Sequence Order: {classItem.sequence_order}</p>
                              <p>Graduating Class: {classItem.leads_to_graduation ? "Yes" : "No"}</p>
                              {classItem.next_class && (
                                <p>Next Class: {classItem.next_class.name}</p>
                              )}
                            </div>
                            <div className="border-t pt-4">
                              <h4 className="font-medium mb-2">Streams</h4>
                              <StreamList classId={classItem.id} />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this class
              and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (deleteConfirm) {
                  deleteMutation.mutate(deleteConfirm);
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
